import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import {
  AutomataService,
  DataType,
  OutputParameter,
  Rule,
  RuleInterface,
  WaitForTriggerFilterInterface,
  WorkflowStepOutputParameterContainer,
} from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil, tap } from 'rxjs/operators';
import { TRIGGER_STEP_ID } from '../../../../../common/constants';
import { convertEntityTypeToDataType } from '../../../../../shared/entities';
import { reconnectPreviousRulesToEventHappenedPath } from '../../../../../shared/variable-path';
import { WORKFLOW_TRIGGERS } from '../../../../component-loader/trigger-definitions';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import {
  convertTimeUnit,
  DelayControlComponent,
  TimeUnit,
} from '../../../common/controls/delay-control/delay-control.component';
import {
  TriggerEditorControl,
  TriggerEditorControlComponent,
} from '../../../common/controls/trigger-editor-control/trigger-editor-control.component';
import {
  OutputParameterSelectorComponent,
  SelectedValues,
} from '../../../common/output-parameter-selector/output-parameter-selector.component';
import { buildStepReferenceString } from '../../../common/utils/step-reference';
import { validDelayUntilDataTypes } from '../../../filters/definitions/dynamic-options/dynamic-type-selectors';
import { DelayUntilEventService } from '../delay-until-event.service';

type Step = 'selectWorkflowStep' | 'selectTrigger';

@Component({
  selector: 'automata-delay-until-event-panel',
  templateUrl: './delay-until-event-panel.component.html',
  styleUrls: ['./delay-until-event-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    TriggerEditorControlComponent,
    OutputParameterSelectorComponent,
    DelayControlComponent,
  ],
})
export class DelayUntilEventPanelComponent
  extends CommonActionPanelComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  validDelayUntilDataTypes = validDelayUntilDataTypes;

  @ViewChild('triggerEditorControl', { static: false })
  triggerEditorControl: TriggerEditorControl;

  waitForTriggerTaskDefinitionId: string;

  // These output parameters are based off of the other steps within the workflow
  // This is important because they are only connected via DataType to whichever trigger the delay until is for
  workflowStepOutputParameterContainers$: Observable<WorkflowStepOutputParameterContainer[]>;
  workflowStepOutputParameterContainer: WorkflowStepOutputParameterContainer;
  selectedOutputParam: OutputParameter;
  selectedStepsData: string;
  triggerParamDataType: DataType;
  destroyed$$ = new Subject<void>();

  form: UntypedFormGroup = new UntypedFormGroup({});

  currentStep: Step = 'selectWorkflowStep';
  showSaveButton = false;
  showNextButton = true;
  showDataPassing = false;

  waitForDataDisplay$: Observable<string>;

  constructor(
    protected readonly delayUntilEventService: DelayUntilEventService,
    protected readonly automataService: AutomataService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.waitForTriggerTaskDefinitionId = this.delayUntilEventService.getWaitForTriggerTaskDefinitionFromStep(
      this.step,
    )?.taskDefinitionId;

    if (this.waitForTriggerTaskDefinitionId) {
      this.next();
    }

    this.triggerParamDataType = convertEntityTypeToDataType(this.automation.entityType);

    this.workflowStepOutputParameterContainers$ = this.automataService
      .getWorkflowStepPredecessorOutputParams(
        this.automation.id,
        (this.precedingSteps || []).concat(TRIGGER_STEP_ID),
        true,
      )
      .pipe(take(1));

    this.selectedStepsData = this.delayUntilEventService.getWaitForEventData(
      this.automation,
      this.step?.waitForTriggerFilter.value,
    );
    this.waitForDataDisplay$ = this.delayUntilEventService.getWaitForDataDisplay(
      this.automation,
      this.step?.waitForTriggerFilter.value,
    );
  }

  outputParamSelected(selected: SelectedValues) {
    this.waitForDataDisplay$ = this.delayUntilEventService.getWaitForDisplayForNameAndType(
      selected?.container?.workflowStep?.name,
      selected?.param?.settings?.name,
    );
    this.selectedStepsData = selected?.container?.workflowStep?.data;
    this.workflowStepOutputParameterContainer = selected?.container;
    this.selectedOutputParam = selected?.param;
  }

  /**
   * The component can be saved as long as the trigger editor says it can be saved.
   */
  ngAfterViewInit(): void {
    this.triggerEditorControl.savingEnabled$
      .pipe(
        takeUntil(this.destroyed$$),
        tap((saveable: boolean) => (this.savingEnabled = saveable)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$$.next(null);
  }

  getRules(stepId?: string): RuleInterface[] {
    // If the trigger choices editor is open, we need to validate and save those rules
    if (this.triggerEditorControl.isEditing) {
      return this.getRulesFromTrigger(stepId);
    }
    return this.step.choices.rules;
  }

  private getRulesFromTrigger(stepId: string): RuleInterface[] {
    let rules = this.triggerEditorControl.triggerChoices.rules;

    if (!rules || rules.length === 0) {
      rules = this.triggerEditorControl.componentRef.instance.getBaseRules();
    }

    rules = this.triggerEditorControl.componentRef.instance.fillRuleValues(rules);

    if (rules.length > 0) {
      // reconnect the new rules to the event happened path
      rules[0].next = this.triggerEditorControl?.originalTriggerChoices?.rules[0]?.next;

      rules = rules
        .map(Rule.fromRuleInterface)
        .map((r) => reconnectPreviousRulesToEventHappenedPath(r, stepId))
        .map((r) => r.toRuleInterface());
    }

    this.triggerEditorControl.triggerChoices.rules = rules;

    return rules;
  }

  getData(): any {
    return this.triggerEditorControl.componentRef.instance.getData();
  }

  getSleepTimeInNanos(): number {
    // Pick a good default if the form isn't ready. Assume it's in hours.
    if (!this.form) {
      return convertTimeUnit(this.step?.sleep || 0, TimeUnit.Hour, TimeUnit.Nano);
    }

    // Selected time may be in days/other unit, so ensure it's converted to nanos.
    return convertTimeUnit(
      this.form.controls?.delay?.get('sleep')?.value,
      this.form.controls?.delay?.get('timeUnit')?.value,
      TimeUnit.Nano,
    );
  }

  getWaitForTriggerTaskDefinitionId(): string {
    return WORKFLOW_TRIGGERS[this.triggerEditorControl?.taskDefinitionId].taskDefinitionId;
  }

  getWaitForTriggerFilter(): WaitForTriggerFilterInterface {
    // We want to use the type from the previous step because that's what they selected in the dropdown
    // But we need to get the ID from the trigger they selected because while it matches type, it might not match IDs
    // NB: This is going to be a problem if we ever have more than one of a single type on a TaskDefinition
    if (this.step?.waitForTriggerFilter) {
      return this.step.waitForTriggerFilter;
    }
    const type: DataType = this.selectedOutputParam?.settings?.dataType;
    const outputParam = this.triggerEditorControl?.taskDefinition?.outputParameters?.find(
      (p) => p.settings.dataType === type,
    );

    return {
      // This defined by the output param of the trigger selected
      key:
        // They pick an output param
        outputParam?.settings?.id ||
        // They open straight to the trigger stuff
        this.step?.waitForTriggerFilter?.key ||
        // Existing automations, templates use-case
        this.selectedOutputParam.settings.id,

      // This is defined on the task definition via the output param of the step picked
      value: buildStepReferenceString(
        this.workflowStepOutputParameterContainer.workflowStep.id,
        this.selectedOutputParam.settings.id,
      ),
    };
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    if (this.triggerEditorControl.componentRef) {
      return this.triggerEditorControl.componentRef.instance.getControlsToValidateOnSave().concat([this.form]);
    }
    return [this.form];
  }

  isFormValid(): boolean {
    if (this.triggerEditorControl.componentRef) {
      if (!this.triggerEditorControl.componentRef.instance.isFormValid()) {
        return false;
      }
    }
    return this.form.valid;
  }

  next(): void {
    this.currentStep = 'selectTrigger';
    this.showNextButton = false;
    this.showSaveButton = true;
    this.showDataPassing = true;
  }

  triggerCleared(): void {
    this.currentStep = 'selectWorkflowStep';
    this.showSaveButton = false;
    this.showNextButton = true;
    this.showDataPassing = false;
    this.step = null;
  }
}
